/**
 * AuthService for SnapHub PWA.
 *
 * Handles login, logout, token management, refresh token rotation,
 * session restoration, and user profile fetching against the Portal API.
 *
 * - Access Token → SessionStorage (via StorageAdapter)
 * - Refresh Token → LocalStorage (via StorageAdapter)
 * - Password is NEVER stored after login attempt
 * - Emits `auth-state-changed` via EventBus on login/logout
 *
 * Validates: Requirements 1.1–1.7, 2.1–2.5, 4.1–4.4, 7.1, 7.2
 */

import { storageAdapter } from '../../infrastructure/storage-adapter';
import { eventBus } from '../../infrastructure/event-bus';
import type { LoginResponse } from '../../shared/models/login-response.model';
import type { RefreshResponse } from '../../shared/models/refresh-response.model';
import type { LoginUser } from '../../shared/models/login-user.model';
import type { TokenPayload } from '../../shared/models/token-payload.model';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Base URL for the Portal API. Reads from the VITE_API_BASE_URL env variable
 * at build time, falling back to an empty string (same-origin requests).
 */
function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_AUTH_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

const API_BASE_URL: string = getApiBaseUrl();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Decode the payload section of a JWT without verifying the signature.
 * Returns `null` when the token is malformed.
 */
export function decodeTokenPayload(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Base64url → Base64 → decode
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as TokenPayload;
  } catch {
    return null;
  }
}

/**
 * Returns `true` when the token's `exp` claim is in the future.
 * A 30-second buffer is applied so we refresh slightly before actual expiry.
 */
export function isTokenValid(token: string): boolean {
  const payload = decodeTokenPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp > nowSeconds + 30; // 30s buffer
}

/**
 * Resolve the user role from a token payload.
 * Checks the direct `role` claim first, then falls back to Keycloak's
 * `realm_access.roles` array. Returns empty string if no role is found.
 */
export function resolveRoleFromPayload(payload: TokenPayload | null): string {
  if (!payload) return '';
  if (payload.role) return payload.role;
  if (payload.realm_access?.roles) {
    const roles = payload.realm_access.roles;
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('employee')) return 'employee';
    if (roles.includes('customer')) return 'customer';
    if (roles.length > 0) return roles[0];
  }
  return '';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Minimum time (ms) before token expiry to trigger a proactive refresh. */
const REFRESH_BUFFER_MS = 60_000; // 60 seconds

/** Maximum retry attempts for a failed refresh. */
const MAX_REFRESH_RETRIES = 2;

/** Base delay (ms) for exponential backoff between refresh retries. */
const RETRY_BASE_DELAY_MS = 1_000;

// ---------------------------------------------------------------------------
// AuthService
// ---------------------------------------------------------------------------

export class AuthService {
  private user: LoginUser | null = null;
  private baseUrl: string;

  /** Timer ID for the proactive token refresh scheduler. */
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;

  /** In-flight refresh promise — used as mutex to prevent parallel refreshes. */
  private refreshPromise: Promise<boolean> | null = null;

  /** Bound handler for visibility change events. */
  private visibilityHandler: (() => void) | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? API_BASE_URL;
  }

  // -- Login ----------------------------------------------------------------

  /**
   * Authenticate with email and password.
   * On success stores tokens and emits auth-state-changed.
   * Returns `null` on success, or an error message string on failure.
   * The password is NOT stored anywhere.
   */
  async loginAsync(email: string, password: string): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseUrl}/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        if (response.status === 401) return 'Ungültige Anmeldedaten. Bitte überprüfen Sie E-Mail und Passwort.';
        if (response.status === 403) return 'Zugriff verweigert. Ihr Konto ist möglicherweise gesperrt.';
        return `Anmeldung fehlgeschlagen (Fehler ${response.status}).`;
      }

      const data: LoginResponse = await response.json();

      if (data.token) {
        storageAdapter.setAccessToken(data.token);
      }
      if (data.refresh_token) {
        storageAdapter.setRefreshToken(data.refresh_token);
      }
      if (data.user) {
        this.user = data.user;
      }

      // Schedule proactive refresh based on server-provided expiry
      if (data.expires_in) {
        this.scheduleProactiveRefresh(data.expires_in);
      } else if (data.token) {
        this.scheduleProactiveRefreshFromToken(data.token);
      }

      // Start monitoring visibility changes for token revalidation
      this.startVisibilityHandler();

      eventBus.emit('auth-state-changed', { authenticated: true });

      // Track login for statistics (fire-and-forget, lazy import to avoid circular dependency)
      if (data.user || data.token) {
        const user = data.user;
        const payload = data.token ? decodeTokenPayload(data.token) : null;
        import('../admin/statistics-tracking.service').then(({ statisticsTrackingService }) => {
          statisticsTrackingService.trackLogin({
            userId: user?.id || payload?.sub || '',
            userEmail: user?.email || payload?.name || email,
            role: user?.role || resolveRoleFromPayload(payload) || '',
            customerId: user?.customerId || payload?.customerId || '',
          });
        }).catch(() => { /* ignore */ });
      }

      return null; // success
    } catch {
      return 'Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.';
    }
  }

  // -- Token Refresh --------------------------------------------------------

  /**
   * Refresh the token pair using the current refresh token.
   * Uses a mutex to prevent parallel refresh requests (token rotation
   * would invalidate one of them). Retries up to MAX_REFRESH_RETRIES
   * with exponential backoff before giving up.
   * Returns `true` on success, `false` on failure.
   */
  async refreshTokenAsync(): Promise<boolean> {
    // Mutex: if a refresh is already in flight, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.executeRefreshWithRetry();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Internal: executes the refresh request with retry logic.
   */
  private async executeRefreshWithRetry(): Promise<boolean> {
    const refreshToken = storageAdapter.getRefreshToken();
    if (!refreshToken) return false;

    for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}/portal/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (response.ok) {
          const data: RefreshResponse = await response.json();
          storageAdapter.setAccessToken(data.token);
          storageAdapter.setRefreshToken(data.refresh_token);
          this.scheduleProactiveRefresh(data.expires_in);
          return true;
        }

        // 401/403 = token reuse detected or revoked — no point retrying
        if (response.status === 401 || response.status === 403) {
          break;
        }

        // Other server errors (500, 502, etc.) — retry with backoff
        if (attempt < MAX_REFRESH_RETRIES) {
          await this.delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        }
      } catch {
        // Network error — retry with backoff
        if (attempt < MAX_REFRESH_RETRIES) {
          await this.delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        }
      }
    }

    // All attempts failed — clear tokens
    this.clearTokens();
    eventBus.emit('auth-state-changed', { authenticated: false });
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // -- Session Restore ------------------------------------------------------

  /**
   * Restore session from stored tokens on app startup.
   * If the access token is still valid, use it.
   * If expired but refresh token exists, attempt a refresh.
   * If both are invalid, clear everything.
   * Also starts the proactive refresh timer and visibility handler.
   */
  async restoreSessionAsync(): Promise<void> {
    const accessToken = storageAdapter.getAccessToken();
    const refreshToken = storageAdapter.getRefreshToken();

    // No tokens at all — nothing to restore
    if (!accessToken && !refreshToken) return;

    // Access token still valid — extract user info from it
    if (accessToken && isTokenValid(accessToken)) {
      this.extractUserFromToken(accessToken);
      this.scheduleProactiveRefreshFromToken(accessToken);
      this.startVisibilityHandler();
      eventBus.emit('auth-state-changed', { authenticated: true });
      return;
    }

    // Access token expired/missing but refresh token exists — try refresh
    if (refreshToken) {
      const success = await this.refreshTokenAsync();
      if (success) {
        const newToken = storageAdapter.getAccessToken();
        if (newToken) {
          this.extractUserFromToken(newToken);
        }
        this.startVisibilityHandler();
        eventBus.emit('auth-state-changed', { authenticated: true });
        return;
      }
    }

    // Everything failed — clear
    this.clearTokens();
    eventBus.emit('auth-state-changed', { authenticated: false });
  }

  // -- Proactive Token Refresh Timer ----------------------------------------

  /**
   * Schedule a proactive refresh based on the `expires_in` value from the server.
   * Refreshes the token 60 seconds before it expires to prevent 401s.
   */
  private scheduleProactiveRefresh(expiresInSeconds: number): void {
    this.clearRefreshTimer();

    // Calculate when to refresh: (expires_in - buffer) in ms
    // Minimum 10 seconds to avoid too-aggressive refresh loops
    const refreshInMs = Math.max(
      (expiresInSeconds * 1000) - REFRESH_BUFFER_MS,
      10_000,
    );

    this.refreshTimerId = setTimeout(async () => {
      const success = await this.refreshTokenAsync();
      if (success) {
        const newToken = storageAdapter.getAccessToken();
        if (newToken) {
          this.extractUserFromToken(newToken);
        }
      }
      // If refresh fails, refreshTokenAsync already handles logout
    }, refreshInMs);
  }

  /**
   * Schedule proactive refresh by reading the exp claim from an existing token.
   */
  private scheduleProactiveRefreshFromToken(token: string): void {
    const payload = decodeTokenPayload(token);
    if (!payload || typeof payload.exp !== 'number') return;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const remainingSeconds = payload.exp - nowSeconds;

    if (remainingSeconds > 0) {
      this.scheduleProactiveRefresh(remainingSeconds);
    }
  }

  /** Clear the proactive refresh timer. */
  private clearRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  // -- Visibility Change Handler --------------------------------------------

  /**
   * Register a handler that checks token validity when the user returns
   * to the app (tab becomes visible again, phone unlocked, etc.).
   */
  private startVisibilityHandler(): void {
    // Only register once
    if (this.visibilityHandler) return;

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.onAppBecameVisible();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Stop listening to visibility changes (called on logout). */
  private stopVisibilityHandler(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Called when the page becomes visible again.
   * Checks if the access token is still valid; if not, triggers a refresh.
   */
  private async onAppBecameVisible(): Promise<void> {
    const accessToken = storageAdapter.getAccessToken();

    // If token is still valid, just reschedule the timer
    if (accessToken && isTokenValid(accessToken)) {
      this.scheduleProactiveRefreshFromToken(accessToken);
      return;
    }

    // Token expired while away — try to refresh
    const refreshToken = storageAdapter.getRefreshToken();
    if (refreshToken) {
      const success = await this.refreshTokenAsync();
      if (success) {
        const newToken = storageAdapter.getAccessToken();
        if (newToken) {
          this.extractUserFromToken(newToken);
        }
      }
    }
  }

  // -- Logout ---------------------------------------------------------------

  /**
   * Logout the current session.
   * Sends the refresh token to the server for revocation, then clears local tokens.
   */
  async logoutAsync(): Promise<void> {
    const refreshToken = storageAdapter.getRefreshToken();

    if (refreshToken) {
      try {
        await fetch(`${this.baseUrl}/portal/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // Server-side revocation failed — still clear locally
      }
    }

    this.clearRefreshTimer();
    this.stopVisibilityHandler();
    this.clearTokens();
    this.user = null;
    eventBus.emit('auth-state-changed', { authenticated: false });
  }

  /**
   * Logout all sessions for the current user.
   * Sends `logout_all: true` to revoke all refresh tokens server-side.
   */
  async logoutAllAsync(): Promise<void> {
    const refreshToken = storageAdapter.getRefreshToken();

    if (refreshToken) {
      try {
        await fetch(`${this.baseUrl}/portal/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken, logout_all: true }),
        });
      } catch {
        // Server-side revocation failed — still clear locally
      }
    }

    this.clearRefreshTimer();
    this.stopVisibilityHandler();
    this.clearTokens();
    this.user = null;
    eventBus.emit('auth-state-changed', { authenticated: false });
  }

  // -- User & Auth State ----------------------------------------------------

  /**
   * Get the currently stored user data.
   * Returns `null` when not authenticated.
   */
  getUser(): LoginUser | null {
    return this.user;
  }

  /**
   * Set the user data (used by SSO callback after successful authentication).
   */
  setUser(user: LoginUser): void {
    this.user = user;
  }

  /**
   * Returns the current access token, or `null` if not available.
   */
  getToken(): string | null {
    return storageAdapter.getAccessToken();
  }

  /**
   * Returns a valid access token, refreshing if necessary.
   * Returns `null` if no valid token can be obtained.
   */
  async getValidTokenAsync(): Promise<string | null> {
    const token = storageAdapter.getAccessToken();
    if (token && isTokenValid(token)) return token;

    const refreshed = await this.refreshTokenAsync();
    if (refreshed) return storageAdapter.getAccessToken();

    return null;
  }

  /**
   * Check whether the user is currently authenticated.
   * A user is authenticated when a non-empty access token exists in storage.
   */
  isAuthenticated(): boolean {
    const token = storageAdapter.getAccessToken();
    return token !== null && token.trim().length > 0;
  }

  /**
   * Subscribe to authentication state changes.
   * The callback receives `true` when authenticated, `false` when not.
   * Returns an unsubscribe function.
   */
  onAuthStateChanged(callback: (authenticated: boolean) => void): () => void {
    return eventBus.on<{ authenticated: boolean }>('auth-state-changed', (detail) => {
      callback(detail.authenticated);
    });
  }

  // -- Change Password ------------------------------------------------------

  /**
   * Change the user's password.
   * Requires the current password and a new password.
   * On success all refresh tokens are invalidated — the user must re-login.
   * Returns `null` on success, or an error message string on failure.
   */
  async changePasswordAsync(currentPassword: string, newPassword: string): Promise<string | null> {
    const token = await this.getValidTokenAsync();
    if (!token) return 'Nicht authentifiziert. Bitte melden Sie sich erneut an.';

    // Read CSRF token from XSRF-TOKEN cookie
    const csrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    try {
      const response = await fetch(`${this.baseUrl}/portal/password`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        if (response.status === 401) return 'Das aktuelle Passwort ist falsch.';
        if (response.status === 400) return 'Das neue Passwort erfüllt nicht die Anforderungen.';
        if (response.status === 422) return 'Das neue Passwort erfüllt nicht die Anforderungen.';
        return `Passwortänderung fehlgeschlagen (Fehler ${response.status}).`;
      }

      return null; // success
    } catch {
      return 'Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.';
    }
  }

  // -- Profile --------------------------------------------------------------

  /**
   * Fetch the user profile from `GET /portal/me`.
   * Requires a valid access token.
   * Updates the internal user state and emits `user-changed`.
   */
  async fetchProfileAsync(): Promise<LoginUser | null> {
    const token = storageAdapter.getAccessToken();
    if (!token) return null;

    try {
      const response = await fetch(`${this.baseUrl}/portal/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return null;

      const userData: LoginUser = await response.json();
      this.user = userData;
      eventBus.emit('user-changed', userData);
      return userData;
    } catch {
      return null;
    }
  }

  // -- Private helpers ------------------------------------------------------

  /**
   * Extract basic user info from the JWT payload and set it on the instance.
   */
  private extractUserFromToken(token: string): void {
    const payload = decodeTokenPayload(token);
    if (!payload) return;

    // Resolve customerId from multiple possible claim names
    const customerId = payload.customerId || payload.customer_id || '';

    // Resolve role via helper (supports direct claim + Keycloak realm_access)
    const role = resolveRoleFromPayload(payload);

    // Resolve name/email from multiple possible claims
    const email = payload.email || payload.preferred_username || payload.name || '';

    this.user = {
      id: payload.sub,
      email,
      role,
      customerId,
      firstName: payload.given_name,
      lastName: payload.family_name,
    };
  }

  /**
   * Clear all stored tokens.
   */
  private clearTokens(): void {
    storageAdapter.clearAll();
    sessionStorage.removeItem('snaphub_login_method');
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

/** Global AuthService shared across the entire application. */
export const authService = new AuthService();
